import React, { useEffect, useState } from 'react';
import { getCurrentUser, subscribeToAuthState, type User } from '../services/graphql/authService';
import { getMyBookings, type BookingRecord, cancelMyBooking } from '../services/graphql/bookingService';
import { useServiceKey } from '../hooks/useServiceKey';
import { Layout } from './Layout';
import { BookingsListLayout } from './BookingsListLayout/BookingsListLayout';

/**
 * MyBookingsPage displays all bookings submitted by the logged-in user.
 */
const MyBookingsPage: React.FC = () => {
  const [bookings, setBookings] = useState<BookingRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const { isValidKey, serviceConfig } = useServiceKey();

  // Function to handle booking cancellation
  const handleCancelBooking = async (bookingId: string, _status: string, reason: string) => {
    try {
      setLoading(true);
      setSuccessMessage(null);
      setError(null);
      console.log(`MyBookingsPage: Cancelling booking ${bookingId} with reason: ${reason}`);

      // Use cancelMyBooking for customer-initiated cancellations
      const { error: cancelError } = await cancelMyBooking(bookingId, reason);

      if (cancelError) {
        console.error('MyBookingsPage: Failed to cancel booking:', cancelError);
        setError(`Failed to cancel booking: ${cancelError.message}`);
        return;
      }

      console.log('MyBookingsPage: Booking cancelled successfully');
      setSuccessMessage('Your booking has been cancelled successfully.');

      // Auto-clear success message after 5 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);

      // Refresh the bookings list
      fetchBookings();
    } catch (err: any) {
      console.error('MyBookingsPage: Exception in handleCancelBooking:', err);
      setError(`An error occurred: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Get current user from GraphQL auth
  useEffect(() => {
    // Get current user
    getCurrentUser()
      .then((userData) => {
        setCurrentUser(userData);
        setUser(userData);
        if (!userData) {
          setLoading(false);
        }
      })
      .catch(() => {
        setCurrentUser(null);
        setUser(null);
        setLoading(false);
      });

    // Subscribe to auth state changes
    const unsubscribe = subscribeToAuthState((userData) => {
      console.log('[MyBookingsPage] Auth state changed:', userData ? 'logged in' : 'logged out');
      setCurrentUser(userData);
      setUser(userData);
      if (!userData) {
        setBookings([]);
      }
    });

    return () => {
      unsubscribe();
    };
  }, []);

  // Function to fetch bookings
  const fetchBookings = async () => {
    if (!user) return;

    setLoading(true);
    setError(null);
    try {
      const { data: bookingRecords, error: fetchError } = await getMyBookings();

      if (fetchError) {
        console.error('MyBookingsPage: Error fetching bookings:', fetchError);
        setError('Failed to fetch bookings.');
        return;
      }

      setBookings(bookingRecords || []);
    } catch (err: any) {
      console.error('MyBookingsPage: Error fetching bookings:', err);
      setError('Failed to fetch bookings.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    fetchBookings();
  }, [user]);

  if (!isValidKey) {
    return (
      <Layout
        currentUser={currentUser}
        serviceConfig={serviceConfig}
      >
        <div className="p-4 max-w-2xl mx-auto">
          <div className="bg-red-100 p-4 rounded-lg text-red-700">
            <h2 className="text-xl font-bold mb-2">Invalid Service Key</h2>
            <p>The service key is not valid. Please use a valid service URL.</p>
          </div>
        </div>
      </Layout>
    );
  }

  const content = (
    <div className="p-8 bg-background text-foreground" data-testid="my-bookings-page">
      <BookingsListLayout
        bookings={bookings}
        loading={loading}
        error={error}
        successMessage={successMessage}
        currentUser={user}
        mode="customer"
        title="My Bookings"
        emptyMessage="No bookings found."
        loadingMessage="Loading your bookings..."
        onCancel={handleCancelBooking}
        showRefreshButton={true}
        onRefresh={fetchBookings}
        className="max-w-6xl mx-auto"
        data-testid="my-bookings-list"
      />
    </div>
  );

  return (
    <Layout
      currentUser={currentUser}
      serviceConfig={serviceConfig}
    >
      <div className="container mx-auto" data-testid="my-bookings-container">
        {content}
      </div>
    </Layout>
  );
};

export default MyBookingsPage;
